import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ClipLoader from 'react-spinners/ClipLoader';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useFetch } from './api';
import { SEVERITY_BY_TYPE, eventLabel, severityDotClass } from './eventTypes';

dayjs.extend(utc);

const MAX_ROWS = 20;

function shortAgo(created) {
  const then = dayjs.utc(created);
  const now = dayjs();
  const mins = now.diff(then, 'minute');
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = now.diff(then, 'hour');
  if (hrs < 24) return `${hrs}h`;
  const days = now.diff(then, 'day');
  return `${days}d`;
}

function HeaderLink({ to, children }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-1 text-xs font-medium text-accent-600 hover:text-accent-700 transition-colors group/nav"
    >
      {children}
      <svg className="w-3 h-3 transition-transform group-hover/nav:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m0 0l-5-5m5 5l-5 5" />
      </svg>
    </Link>
  );
}

function HomepageActivityFeed({ integrationLists, days = 1 }) {
  const trackstar = useFetch();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  const nameToLogo = {};
  const nameToDisplay = {};
  if (integrationLists) {
    Object.values(integrationLists).forEach((list) => {
      Object.entries(list).forEach(([name, integration]) => {
        nameToLogo[name] = integration.logo_url;
        nameToDisplay[name] = integration.display_name;
      });
    });
  }

  useEffect(() => {
    // guard against a slower earlier-range request overwriting the current one
    let cancelled = false;
    const startDate = dayjs().utc().subtract(days, 'day');
    const endDate = dayjs().utc();
    setLoading(true);
    setFetchError(false);
    trackstar(`internal/events?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`)
      .then((response) => {
        if (cancelled) return;
        if (!response || response.error || !Array.isArray(response.data)) {
          setFetchError(true);
          setEvents([]);
          return;
        }
        const enriched = [];
        response.data.forEach((event) => {
          const severity = SEVERITY_BY_TYPE[event.type] || 'info';
          // only surface events that need attention (errors + warnings), not routine info
          if (severity !== 'info') enriched.push({ ...event, severity });
        });
        enriched.sort((a, b) => dayjs(b.created_at).unix() - dayjs(a.created_at).unix());
        setEvents(enriched.slice(0, MAX_ROWS));
      })
      .catch(() => {
        if (cancelled) return;
        setFetchError(true);
        setEvents([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [days]);

  let body;
  if (loading) {
    body = (
      <div className="flex-1 flex items-center justify-center">
        <ClipLoader size={24} color="#D4532A" />
      </div>
    );
  } else if (fetchError) {
    body = (
      <div className="flex-1 flex items-center justify-center text-center px-2">
        <p className="text-sm text-surface-500">Couldn&apos;t load recent activity.</p>
      </div>
    );
  } else if (events.length === 0) {
    body = (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-2">
        <p className="text-sm font-medium text-surface-600 mb-1">Nothing needs attention</p>
        <p className="text-xs text-surface-400">Failed installs, connection errors, and stuck syncs will show up here.</p>
      </div>
    );
  } else {
    body = (
      <div className="flex-1 overflow-y-auto -mr-2 pr-2">
        {events.map((event) => (
          <Link
            key={event.id}
            to={`/events?id=${event.id}`}
            className="flex items-start gap-3 py-2.5 group"
          >
            <div className="flex-shrink-0 w-7 h-7 rounded-md bg-surface-100 border border-surface-200 flex items-center justify-center mt-0.5">
              {nameToLogo[event.integration_name] ? (
                <img src={nameToLogo[event.integration_name]} alt={event.integration_name} className="h-4 w-4 object-contain" />
              ) : (
                <div className="h-4 w-4 bg-surface-200 rounded" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${severityDotClass(event.severity)}`} />
                <p className="text-sm font-medium text-surface-700 group-hover:text-surface-900 transition-colors truncate min-w-0">
                  {eventLabel(event.type)}
                </p>
                <span className="text-[11px] text-surface-400 flex-shrink-0">{shortAgo(event.created_at)}</span>
                <div className="flex-1" />
                <svg className="w-4 h-4 text-surface-300 group-hover:text-surface-500 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p className="text-xs text-surface-400 truncate ml-3.5">
                {nameToDisplay[event.integration_name] || event.integration_name}
              </p>
            </div>
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex justify-between items-baseline mb-4">
        <h2 className="text-sm font-semibold text-surface-800">Important Activity</h2>
        <div className="flex items-center gap-4">
          <HeaderLink to="/events">All events</HeaderLink>
          <HeaderLink to="/events?severity=important">Important</HeaderLink>
        </div>
      </div>
      {body}
    </div>
  );
}

export default HomepageActivityFeed;
